<template>
  <div>
    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12 col-xl-12" style="margin-top: 20px;">
          <div class="card">
            <div class="card-header diot-header d-flex align-items-center justify-content-between flex-wrap">
              <div>
                <h3 class="mb-0">Papel de Trabajo (DIOT)</h3>
                <small class="text-muted">Resumen de operaciones con terceros (proveedores) para antesala DIOT.</small>
              </div>

              <div class="diot-actions d-flex align-items-center flex-wrap">
                <button class="btn btn-outline-info btn-sm" :disabled="loading || saving || recalculating || deletingSupplier" @click="openManualTypeModal">Proveedor Manual</button>
                <button class="btn btn-outline-success btn-sm" :disabled="loading || saving || !summary.length" @click="saveDiot">Guardar</button>
                <button class="btn btn-outline-warning btn-sm" :disabled="loading || recalculating || !summary.length" @click="openRecalculateModal">Reiniciar</button>
                <div class="btn-group diot-export-group" @click.stop>
                  <button
                    class="btn btn-primary btn-sm dropdown-toggle"
                    type="button"
                    :aria-expanded="exportMenuOpen"
                    :disabled="loading || saving || recalculating || deletingSupplier || manualSupplierSaving || (!details.length && !summary.length)"
                    @click="toggleExportMenu"
                  >Exportar</button>
                  <ul v-if="exportMenuOpen" class="dropdown-menu dropdown-menu-end show">
                    <li>
                      <button
                        type="button"
                        class="dropdown-item"
                        :class="{ disabled: loading || saving || recalculating || deletingSupplier || manualSupplierSaving || !details.length }"
                        :aria-disabled="loading || saving || recalculating || deletingSupplier || manualSupplierSaving || !details.length"
                        @click="handleExportOption('excel')"
                      >Descargar Excel</button>
                    </li>
                    <li>
                      <button
                        type="button"
                        class="dropdown-item"
                        :class="{ disabled: loading || saving || recalculating || deletingSupplier || manualSupplierSaving || !summary.length }"
                        :aria-disabled="loading || saving || recalculating || deletingSupplier || manualSupplierSaving || !summary.length"
                        @click="handleExportOption('txt')"
                      >Descargar TXT</button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="card-body">
              <div v-if="loading" class="text-center py-3">Generando papel de trabajo...</div>
              <div v-else-if="error" class="alert alert-warning py-2">{{ error }}</div>
              <div v-else>
                <div v-if="saveMessage" class="alert alert-success py-2">{{ saveMessage }}</div>
                <div class="row g-3 mb-3">
                  <div class="col-sm-6 col-xl-3">
                    <div class="diot-kpi diot-kpi-split">
                      <div class="diot-kpi-metric">
                        <span>Proveedores</span>
                        <strong>{{ formatInteger(totals.providers_count) }}</strong>
                      </div>
                      <div class="diot-kpi-metric diot-kpi-metric-right">
                        <span>CFDIs</span>
                        <strong>{{ formatInteger(totals.cfdis_count) }}</strong>
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-6 col-xl-3">
                    <div class="diot-kpi">
                      <span>Total actividades</span>
                      <strong>{{ formatCurrency(totalActivitiesMxn) }}</strong>
                      <button type="button" class="btn btn-outline-primary btn-sm diot-kpi-button" title="Ver desglose de actividades" aria-label="Ver desglose de actividades" @click="openIvaBreakdownModal"><i class="fa fa-search"></i></button>
                    </div>
                  </div>
                  <div class="col-sm-6 col-xl-3">
                    <div class="diot-kpi">
                      <span>IVA acreditable</span>
                      <strong>{{ formatCurrency(totalCreditableIvaMxn) }}</strong>
                      <button type="button" class="btn btn-outline-primary btn-sm diot-kpi-button" title="Ver impuesto calculado" aria-label="Ver impuesto calculado" @click="openIvaTaxBreakdownModal"><i class="fa fa-search"></i></button>
                    </div>
                  </div>
                  <div class="col-sm-6 col-xl-3">
                    <div class="diot-kpi">
                      <span>IVA retenido</span>
                      <strong>{{ formatCurrency(totalRetainedIvaMxn) }}</strong>
                    </div>
                  </div>
                </div>

                <h6 class="mb-2">Resumen por proveedor</h6>
                <div class="table-responsive mb-4 diot-wide-wrap">
                  <table class="table table-sm diot-table diot-wide-table">
                    <thead>
                      <tr>
                        <th v-for="column in diotColumns" :key="column.key">
                          <div
                            class="diot-th-wrap"
                            data-bs-toggle="popover"
                            data-bs-trigger="hover focus"
                            data-bs-html="true"
                            data-bs-placement="top"
                            data-bs-custom-class="diot-help-popover"
                            :data-bs-content="getColumnHelp(column)"
                            tabindex="0"
                            role="button"
                            :aria-label="`Ayuda ${column.code} ${column.label}`"
                          >
                            <span class="diot-th-code">{{ column.code }}</span>
                            <span class="diot-th-label">{{ column.label }}</span>
                          </div>
                        </th>
                        <th class="diot-actions-col">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!summary.length">
                        <td :colspan="diotColumns.length + 1" class="text-center">Sin información para el periodo seleccionado.</td>
                      </tr>
                      <tr v-for="item in summary" :key="item.supplier_key || `${item.provider_rfc}-${item.provider_name}`">
                        <td v-for="column in diotColumns" :key="`${item.supplier_key || `${item.provider_rfc}-${item.provider_name}`}-${column.key}`" class="diot-cell">
                          <template v-if="column.type === 'select'">
                            <select
                              class="form-select form-select-sm diot-select"
                              :value="getDiotValue(item, column.key)"
                              @change="setDiotValue(item, column.key, $event.target.value, 'select')"
                            >
                              <option
                                v-for="option in column.options || []"
                                :key="`${column.key}-${getOptionValue(option)}`"
                                :value="getOptionValue(option)"
                              >{{ getOptionLabel(option) }}</option>
                            </select>
                          </template>
                          <template v-else-if="column.type === 'readonly'">
                            <input
                              type="text"
                              class="form-control form-control-sm diot-input diot-readonly"
                              :value="getDiotValue(item, column.key) || '-'"
                              readonly
                            >
                          </template>
                          <template v-else>
                            <input
                              :type="column.type === 'number' ? 'number' : 'text'"
                              class="form-control form-control-sm diot-input"
                              :step="column.type === 'number' ? '1' : null"
                              :value="getDiotValue(item, column.key)"
                              @input="setDiotValue(item, column.key, $event.target.value, column.type)"
                            >
                          </template>
                        </td>
                        <td class="diot-cell diot-actions-col">
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                            title="Eliminar proveedor"
                            aria-label="Eliminar proveedor"
                            :disabled="loading || saving || recalculating || deletingSupplier"
                            @click="openDeleteSupplierModal(item)"
                          ><i class="fa fa-trash"></i></button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CfdiPreviewModal
      :visible="cfdiPreviewModal.visible"
      :uuid="cfdiPreviewModal.uuid"
      :rfc="rfc"
      @close="closeCfdiPreview"
    />

    <ConfirmActionModal
      :show="deleteSupplierModal.visible"
      title="Eliminar proveedor de DIOT"
      :message="deleteSupplierModal.message"
      confirmText="Eliminar"
      cancelText="Cancelar"
      @cancel="closeDeleteSupplierModal"
      @confirm="confirmDeleteSupplier"
    />

    <ConfirmActionModal
      :show="recalculateModal.visible"
      title="Recalcular DIOT"
      :message="recalculateModal.message"
      confirmText="Recalcular"
      cancelText="Cancelar"
      @cancel="closeRecalculateModal"
      @confirm="confirmRecalculateDiot"
    />

    <teleport to="body">
      <div
        v-if="manualTypeModal.visible"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        @click.self="closeManualTypeModal"
      >
        <div class="modal-dialog modal-md modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Agregar proveedor manual</h5>
              <button type="button" class="btn-close" aria-label="Cerrar" @click="closeManualTypeModal"></button>
            </div>
            <div class="modal-body">
              <p class="mb-2">Selecciona el tipo de proveedor:</p>
              <div class="d-grid gap-2">
                <button type="button" class="btn btn-outline-primary" @click="selectManualSupplierType('national')">Proveedor Nacional</button>
                <button type="button" class="btn btn-outline-primary" @click="selectManualSupplierType('international')">Proveedor Internacional</button>
                <button type="button" class="btn btn-outline-primary" @click="selectManualSupplierType('global')">Proveedor Global</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="manualTypeModal.visible" class="modal-backdrop fade show"></div>
    </teleport>

    <teleport to="body">
      <div
        v-if="manualSupplierModal.visible"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        @click.self="closeManualSupplierModal"
      >
        <div class="modal-dialog modal-md modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ manualSupplierModal.title }}</h5>
              <button type="button" class="btn-close" aria-label="Cerrar" @click="closeManualSupplierModal"></button>
            </div>
            <div class="modal-body">
              <div v-if="manualSupplierFormError" class="alert alert-warning py-2 mb-3">{{ manualSupplierFormError }}</div>
              <div class="row g-2">
                <div class="col-12" v-if="manualSupplierForm.type === 'national' || manualSupplierForm.type === 'global'">
                  <label class="form-label">Tipo de Operación (02)</label>
                  <select v-model="manualSupplierForm.operation_type" class="form-select">
                    <option value="02">02 - Enaj. de Bienes</option>
                    <option value="03">03 - Prest. de Serv. Prof.</option>
                    <option value="06">06 - Uso o goce temp. de bienes</option>
                    <option value="07">07 - Importación de bienes o servicios</option>
                    <option value="08">08 - Importación por transferencia virtual</option>
                    <option value="85">85 - Otros</option>
                    <option value="87">87 - Ope. globales</option>
                  </select>
                </div>

                <div class="col-12" v-if="manualSupplierForm.type === 'national'">
                  <label class="form-label">RFC (03)</label>
                  <input
                    v-model="manualSupplierForm.provider_rfc"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': !!manualSupplierErrors.provider_rfc }"
                    maxlength="13"
                  />
                  <div v-if="manualSupplierErrors.provider_rfc" class="invalid-feedback d-block">{{ manualSupplierErrors.provider_rfc }}</div>
                </div>

                <div class="col-12" v-if="manualSupplierForm.type === 'international'">
                  <label class="form-label">Nombre del Proveedor (05)</label>
                  <input
                    v-model="manualSupplierForm.provider_name"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': !!manualSupplierErrors.provider_name }"
                  />
                  <div v-if="manualSupplierErrors.provider_name" class="invalid-feedback d-block">{{ manualSupplierErrors.provider_name }}</div>
                </div>

                <div class="col-12" v-if="manualSupplierForm.type === 'international'">
                  <label class="form-label">ID Fiscal (04)</label>
                  <input
                    v-model="manualSupplierForm.tax_id"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': !!manualSupplierErrors.tax_id }"
                  />
                  <div v-if="manualSupplierErrors.tax_id" class="invalid-feedback d-block">{{ manualSupplierErrors.tax_id }}</div>
                </div>

                <div class="col-12" v-if="manualSupplierForm.type === 'international'">
                  <label class="form-label">Pais de residencia fiscal (06)</label>
                  <input
                    v-model="manualSupplierForm.f06_pais_residencia"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': !!manualSupplierErrors.f06_pais_residencia }"
                  />
                  <div v-if="manualSupplierErrors.f06_pais_residencia" class="invalid-feedback d-block">{{ manualSupplierErrors.f06_pais_residencia }}</div>
                </div>

                <div class="col-12">
                  <label class="form-label">Actividades 16% (12)</label>
                  <input v-model.number="manualSupplierForm.f12_actividades_pagadas_resto" @input="sanitizeManualAmount('f12_actividades_pagadas_resto', { syncIva: true })" type="number" class="form-control" step="1" min="0" />
                </div>
                <div class="col-12">
                  <label class="form-label">IVA acreditable (22)</label>
                  <input v-model.number="manualSupplierForm.f22_iva_acreditable_resto" @input="sanitizeManualAmount('f22_iva_acreditable_resto')" type="number" class="form-control" step="1" min="0" />
                </div>
                <div class="col-12">
                  <label class="form-label">Actos exentos (50)</label>
                  <input v-model.number="manualSupplierForm.f50_actos_exentos" @input="sanitizeManualAmount('f50_actos_exentos')" type="number" class="form-control" step="1" min="0" />
                </div>
                <div class="col-12">
                  <label class="form-label">Actos tasa 0% (51)</label>
                  <input v-model.number="manualSupplierForm.f51_actos_tasa_0" @input="sanitizeManualAmount('f51_actos_tasa_0')" type="number" class="form-control" step="1" min="0" />
                </div>
                <div class="col-12">
                  <label class="form-label">Actos no objeto (52)</label>
                  <input v-model.number="manualSupplierForm.f52_actos_no_objeto_nacional" @input="sanitizeManualAmount('f52_actos_no_objeto_nacional')" type="number" class="form-control" step="1" min="0" />
                </div>
                <div class="col-12" v-if="manualSupplierErrors.amounts">
                  <div class="invalid-feedback d-block">{{ manualSupplierErrors.amounts }}</div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light" :disabled="manualSupplierSaving" @click="closeManualSupplierModal">Cancelar</button>
              <button type="button" class="btn btn-primary" :disabled="manualSupplierSaving" @click="submitManualSupplier">Agregar</button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="manualSupplierModal.visible" class="modal-backdrop fade show"></div>
    </teleport>

    <teleport to="body">
      <div
        v-if="ivaBreakdownModal.visible"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        @click.self="closeIvaBreakdownModal"
      >
        <div class="modal-dialog modal-md modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Desglose de Actividades</h5>
              <button type="button" class="btn-close" aria-label="Cerrar" @click="closeIvaBreakdownModal"></button>
            </div>
            <div class="modal-body">
              <div class="row g-2">
                <div class="col-12" v-for="item in ivaBreakdownItems" :key="item.key">
                  <div class="diot-modal-kpi">
                    <span>{{ item.label }}</span>
                    <strong>{{ formatCurrency(item.value) }}</strong>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light" @click="closeIvaBreakdownModal">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="ivaBreakdownModal.visible" class="modal-backdrop fade show"></div>
    </teleport>

    <teleport to="body">
      <div
        v-if="ivaTaxBreakdownModal.visible"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        @click.self="closeIvaTaxBreakdownModal"
      >
        <div class="modal-dialog modal-md modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Desglose de Impuestos</h5>
              <button type="button" class="btn-close" aria-label="Cerrar" @click="closeIvaTaxBreakdownModal"></button>
            </div>
            <div class="modal-body">
              <div class="row g-2">
                <div class="col-12" v-for="item in ivaTaxBreakdownItems" :key="item.key">
                  <div class="diot-modal-kpi">
                    <span>{{ item.label }}</span>
                    <strong>{{ formatCurrency(item.value) }}</strong>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light" @click="closeIvaTaxBreakdownModal">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="ivaTaxBreakdownModal.visible" class="modal-backdrop fade show"></div>
    </teleport>
  </div>
</template>

<script>
import CfdiPreviewModal from '@/components/cfdis/CfdiPreviewModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import { Popover } from 'bootstrap';
import { GLOBAL_PERIOD_EVENT, getGlobalPeriod } from '@/services/globalPeriod';
import ws from '@/services/cfdis';

export default {
  name: 'DiotWorkpaper',
  components: {
    CfdiPreviewModal,
    ConfirmActionModal,
  },
  data() {
    return {
      rfc: '',
      period: '',
      loading: false,
      saving: false,
      recalculating: false,
      deletingSupplier: false,
      manualSupplierSaving: false,
      exportMenuOpen: false,
      error: '',
      saveMessage: '',
      helpPopovers: [],
      cfdiPreviewModal: {
        visible: false,
        uuid: '',
      },
      supplierSelections: {},
      deletedSuppliers: [],
      deleteSupplierModal: {
        visible: false,
        item: null,
        message: '¿Deseas eliminar este proveedor de la DIOT para el periodo actual?',
      },
      recalculateModal: {
        visible: false,
        message: 'Se perderán los cambios guardados del periodo y se recalculará la DIOT. ¿Deseas continuar?',
      },
      manualTypeModal: {
        visible: false,
      },
      manualSupplierModal: {
        visible: false,
        title: 'Agregar proveedor manual',
      },
      manualSupplierForm: {
        type: 'national',
        provider_rfc: '',
        operation_type: '85',
        provider_name: '',
        tax_id: '',
        f06_pais_residencia: '',
        f12_actividades_pagadas_resto: 0,
        f22_iva_acreditable_resto: 0,
        f50_actos_exentos: 0,
        f51_actos_tasa_0: 0,
        f52_actos_no_objeto_nacional: 0,
      },
      manualSupplierErrors: {},
      manualSupplierFormError: '',
      ivaBreakdownModal: {
        visible: false,
      },
      ivaTaxBreakdownModal: {
        visible: false,
      },
      diotColumns: [
        {
          code: '01',
          key: 'f01_tipo_tercero',
          label: 'Tipo de tercero',
          type: 'select',
          options: [
            { value: '04', label: '04 - Nacional' },
            { value: '05', label: '05 - Extranjero' },
            { value: '15', label: '15 - Global' },
          ],
          help: '04 - Nacional<br>05 - Extranjero<br>15 - Global',
        },
        {
          code: '02',
          key: 'f02_tipo_operacion',
          label: 'Tipo de operación',
          type: 'select',
          options: [
            { value: '02', label: '02 - Enaj. de Bienes' },
            { value: '03', label: '03 - Prest. de Serv. Prof.' },
            { value: '06', label: '06 - Uso o goce temp. de bienes' },
            { value: '07', label: '07 - Importación de bienes o servicios' },
            { value: '08', label: '08 - Importación por transferencia virtual' },
            { value: '85', label: '85 - Otros' },
            { value: '87', label: '87 - Ope. globales' },
          ],
          help: '02 - Enaj. de Bienes<br>03 - Prest. de Serv. Prof.<br>06 - Uso o goce temp. de bienes<br>07 - Importación de bienes o servicios<br>08 - Importación por transferencia virtual<br>85 - Otros<br>87 - Ope. globales',
        },
        { code: '03', key: 'f03_rfc', label: 'Registro Federal deContribuyentes', type: 'readonly' },
        { code: '04', key: 'f04_id_fiscal', label: 'Número de identificación fiscal', type: 'text' },
        { code: '05', key: 'f05_nombre_extranjero', label: 'Nombre del extranjero', type: 'text' },
        { code: '06', key: 'f06_pais_residencia', label: 'País o jurisdicción de residencia fiscal', type: 'text' },
        { code: '07', key: 'f07_jurisdiccion_fiscal', label: 'Especificar lugar de jurisdicción fiscal', type: 'text' },
        { code: '08', key: 'f08_actividades_pagadas_rfn', label: 'Valor total de actos o actividades pagadas / Actos o actividades pagados en la región fronteriza norte', type: 'number' },
        { code: '09', key: 'f09_devoluciones_rfn', label: 'Devoluciones, descuentos y bonificaciones / Actos o actividades pagados en la región fronteriza norte', type: 'number' },
        { code: '10', key: 'f10_actividades_pagadas_rfs', label: 'Valor total de actos o actividades pagadas / Actos o actividades pagados en la región fronteriza sur', type: 'number' },
        { code: '11', key: 'f11_devoluciones_rfs', label: 'Devoluciones, descuentos y bonificaciones / Actos o actividades pagados en la región fronteriza sur', type: 'number' },
        { code: '12', key: 'f12_actividades_pagadas_resto', label: 'Valor total de actos o actividades pagadas / Actos o actividades totales pagados a la tasa del 16% de IVA', type: 'number' },
        { code: '13', key: 'f13_devoluciones_resto', label: 'Devoluciones, descuentos y bonificaciones / Actos o actividades totales pagados a la tasa del 16% de IVA', type: 'number' },
        { code: '14', key: 'f14_importaciones_16', label: 'Valor total de actos o actividades pagadas / Actos o actividades pagados en la importación por aduana de bienes tangibles a la tasa del 16% de IVA', type: 'number' },
        { code: '15', key: 'f15_devoluciones_importaciones', label: 'Devoluciones, descuentos y bonificaciones / Actos o actividades pagados en la importación por aduana de bienes tangibles a la tasa del 16% de IVA', type: 'number' },
        { code: '16', key: 'f16_importaciones_intangibles_16', label: 'Valor total de actos o actividades pagadas / Actos o actividades pagados en la importación de bienes intangibles y servicios a la tasa del 16% de IVA', type: 'number' },
        { code: '17', key: 'f17_devoluciones_importaciones_intangibles', label: 'Devoluciones, descuentos y bonificaciones / Actos o actividades pagados en la importación de bienes intangibles y servicios a la tasa del 16% de IVA', type: 'number' },
        { code: '18', key: 'f18_iva_acreditable_rfn', label: 'Exclusivamente de actividades gravadas / Monto del IVA pagado acreditable en la región fronteriza norte', type: 'number' },
        { code: '19', key: 'f19_proporcion_rfn', label: 'Asociado a actividades por las cuales se aplicó una proporción / Monto del IVA pagado acreditable en la región fronteriza norte', type: 'number' },
        { code: '20', key: 'f20_iva_acreditable_rfs', label: 'Exclusivamente de actividades gravadas / Monto del IVA pagado acreditable en la región fronteriza sur', type: 'number' },
        { code: '21', key: 'f21_proporcion_rfs', label: 'Asociado a actividades por las cuales se aplicó una proporción / Monto del IVA pagado acreditable en la región fronteriza sur', type: 'number' },
        { code: '22', key: 'f22_iva_acreditable_resto', label: 'Exclusivamente de actividades gravadas / Monto del IVA pagado (acreditable a la tasa del 16%)', type: 'number' },
        { code: '23', key: 'f23_proporcion_resto', label: 'Asociado a actividades por las cuales se aplicó una proporción / Monto del IVA pagado (acreditable a la tasa del 16%)', type: 'number' },
        { code: '24', key: 'f24_iva_acreditable_import_tang', label: 'Exclusivamente de actividades gravadas / Monto del IVA pagado (acreditable en la importación por aduana de bienes tangibles a la tasa del 16%)', type: 'number' },
        { code: '25', key: 'f25_proporcion_import_tang', label: 'Asociado a actividades por las cuales se aplicó una proporción/ Monto del IVA pagado (acreditable en la importación por aduana de bienes tangibles a la tasa del 16%)', type: 'number' },
        { code: '26', key: 'f26_iva_acreditable_import_intang', label: 'Exclusivamente de actividades gravadas / Monto del IVA pagado (acreditable en la importación de bienes intangibles y servicios a la tasa del 16%)', type: 'number' },
        { code: '27', key: 'f27_proporcion_import_intang', label: 'Asociado a actividades por las cuales se aplicó una proporción / Monto del IVA pagado (acreditable en la importación de bienes intangibles y servicios a la tasa del 16%)', type: 'number' },
        { code: '28', key: 'f28_iva_no_acreditable_prop_rfn', label: 'Asociado a actividades por las cuales se aplicó una proporción / Monto del IVA pagado no acreditable en la región fronteriza norte', type: 'number' },
        { code: '29', key: 'f29_iva_no_acreditable_no_req_rfn', label: 'Asociado a actividades que no cumple con requisitos / Monto del IVA pagado no acreditable en la región fronteriza norte', type: 'number' },
        { code: '30', key: 'f30_iva_no_acreditable_exentas_rfn', label: 'Asociado a actividades exentas / Monto del IVA pagado no acreditable en la región fronteriza norte', type: 'number' },
        { code: '31', key: 'f31_iva_no_acreditable_no_objeto_rfn', label: 'Asociado a actividades no objeto / Monto del IVA pagado no acreditable en la región fronteriza norte', type: 'number' },
        { code: '32', key: 'f32_iva_no_acreditable_prop_rfs', label: 'Asociado a actividades por las cuales se aplicó una proporción / Monto del IVA pagado no acreditable en la región fronteriza sur', type: 'number' },
        { code: '33', key: 'f33_iva_no_acreditable_no_req_rfs', label: 'Asociado a actividades que no cumple con requisitos / Monto del IVA pagado no acreditable en la región fronteriza sur', type: 'number' },
        { code: '34', key: 'f34_iva_no_acreditable_exentas_rfs', label: 'Asociado a actividades exentas / Monto del IVA pagado no acreditable en la región fronteriza sur', type: 'number' },
        { code: '35', key: 'f35_iva_no_acreditable_no_objeto_rfs', label: 'Asociado a actividades no objeto / Monto del IVA pagado no acreditable en la región fronteriza sur', type: 'number' },
        { code: '36', key: 'f36_iva_no_acreditable_prop_resto', label: 'Asociado a actividades por las cuales se aplicó una proporción / Monto del IVA pagado no acreditable en el resto del país', type: 'number' },
        { code: '37', key: 'f37_iva_no_acreditable_no_req_resto', label: 'Asociado a actividades que no cumple con requisitos / Monto del IVA pagado no acreditable en el resto del país', type: 'number' },
        { code: '38', key: 'f38_iva_no_acreditable_exentas_resto', label: 'Asociado a actividades exentas / Monto del IVA pagado no acreditable en el resto del país', type: 'number' },
        { code: '39', key: 'f39_iva_no_acreditable_no_objeto_resto', label: 'Asociado a actividades no objeto / Monto del IVA pagado no acreditable en el resto del país', type: 'number' },
        { code: '40', key: 'f40_iva_no_acreditable_prop_imp_tang', label: 'Asociado a actividades por las cuales se aplicó una proporción / Monto del IVA pagado no acreditable en la importación de bienes tangibles', type: 'number' },
        { code: '41', key: 'f41_iva_no_acreditable_no_req_imp_tang', label: 'Asociado a actividades que no cumple con requisitos / Monto del IVA pagado no acreditable en la importación de bienes tangibles', type: 'number' },
        { code: '42', key: 'f42_iva_no_acreditable_exentas_imp_tang', label: 'Asociado a actividades exentas / Monto del IVA pagado no acreditable en la importación de bienes tangibles', type: 'number' },
        { code: '43', key: 'f43_iva_no_acreditable_no_objeto_imp_tang', label: 'Asociado a actividades no objeto / Monto del IVA pagado no acreditable en la importación de bienes tangibles', type: 'number' },
        { code: '44', key: 'f44_iva_no_acreditable_prop_imp_intang', label: 'Asociado a actividades por las cuales se aplicó una proporción / Monto del IVA pagado no acreditable en la importación de bienes intangibles', type: 'number' },
        { code: '45', key: 'f45_iva_no_acreditable_no_req_imp_intang', label: 'Asociado a actividades que no cumple con requisitos / Monto del IVA pagado no acreditable en la importación de bienes intangibles', type: 'number' },
        { code: '46', key: 'f46_iva_no_acreditable_exentas_imp_intang', label: 'Asociado a actividades exentas / Monto del IVA pagado no acreditable en la importación de bienes intangibles', type: 'number' },
        { code: '47', key: 'f47_iva_no_acreditable_no_objeto_imp_intang', label: 'Asociado a actividades no objeto / Monto del IVA pagado no acreditable en la importación de bienes intangibles', type: 'number' },
        { code: '48', key: 'f48_iva_retenido_contribuyente', label: 'IVA retenido por el contribuyente', type: 'number' },
        { code: '49', key: 'f49_importacion_exentos', label: 'Valor de actos o actividades pagados en la importación de bienes y servicios por los que no pagará el IVA (Exentos)', type: 'number' },
        { code: '50', key: 'f50_actos_exentos', label: 'Valor de actos o actividades pagados por los que no se pagará el IVA (Exentos)', type: 'number' },
        { code: '51', key: 'f51_actos_tasa_0', label: 'Valor de demás actos o actividades pagados a la tasa del 0% de IVA', type: 'number' },
        { code: '52', key: 'f52_actos_no_objeto_nacional', label: 'Valor de actos o actividades no objeto del IVA realizados en territorio nacional', type: 'number' },
        { code: '53', key: 'f53_actos_no_objeto_sin_establecimiento', label: 'Valor de actos o actividades no objeto del IVA por no contar con establecimiento en territorio nacional', type: 'number' },
        {
          code: '54',
          key: 'f54_efectos_fiscales',
          label: 'Manifiesto que se dio efectos fiscales a los comprobantes que amparan las operaciones realizadas con el proveedor, detalle',
          type: 'select',
          options: [
            { value: '01', label: '01 - SI' },
            { value: '02', label: '02 - NO' },
          ],
        },
      ],
      summary: [],
      details: [],
      totals: {
        providers_count: 0,
        cfdis_count: 0,
        total_mxn: 0,
        base_16_mxn: 0,
        base_8_mxn: 0,
        base_exento_mxn: 0,
        base_0_mxn: 0,
        ret_iva_mxn: 0,
        iva_16_mxn: 0,
        iva_8_mxn: 0,
      },
      source: 'calculated',
      savedAt: null,
    };
  },
  mounted() {
    this.rfc = localStorage.getItem('rfc') || '';
    this.period = getGlobalPeriod();
    window.addEventListener(GLOBAL_PERIOD_EVENT, this.handleGlobalPeriodChanged);
    document.addEventListener('click', this.handleDocumentClick);
    this.fetchReport();
  },
  beforeUnmount() {
    this.disposeHelpPopovers();
    window.removeEventListener(GLOBAL_PERIOD_EVENT, this.handleGlobalPeriodChanged);
    document.removeEventListener('click', this.handleDocumentClick);
  },
  computed: {
    totalActivitiesMxn() {
      const act16 = this.roundDiotInteger(this.totals?.base_16_mxn || 0);
      const act8 = this.roundDiotInteger(this.totals?.base_8_mxn || 0);
      const exento = this.roundDiotInteger(this.totals?.base_exento_mxn || 0);
      const tasa0 = this.roundDiotInteger(this.totals?.base_0_mxn || 0);

      return act16 + act8 + exento + tasa0;
    },
    totalCreditableIvaMxn() {
      const iva16 = this.roundDiotInteger(this.totals?.iva_16_mxn || 0);
      const iva8 = this.roundDiotInteger(this.totals?.iva_8_mxn || 0);

      return iva16 + iva8;
    },
    totalRetainedIvaMxn() {
      return this.roundDiotInteger(this.totals?.ret_iva_mxn || 0);
    },
    ivaBreakdownItems() {
      const act16 = this.roundDiotInteger(this.totals?.base_16_mxn || 0);
      const act8 = this.roundDiotInteger(this.totals?.base_8_mxn || 0);
      const exento = this.roundDiotInteger(this.totals?.base_exento_mxn || 0);
      const tasa0 = this.roundDiotInteger(this.totals?.base_0_mxn || 0);

      return [
        {
          key: 'act16',
          label: 'Actividades al 16%',
          value: act16,
        },
        {
          key: 'act8',
          label: 'Actividades al 8%',
          value: act8,
        },
        {
          key: 'exento',
          label: 'Actividades Exento',
          value: exento,
        },
        {
          key: 'tasa0',
          label: 'Actividades Tasa 0%',
          value: tasa0,
        }
      ];
    },
    ivaTaxBreakdownItems() {
      const iva16 = this.roundDiotInteger(this.totals?.iva_16_mxn || 0);
      const iva8 = this.roundDiotInteger(this.totals?.iva_8_mxn || 0);

      return [
        {
          key: 'iva16',
          label: 'IVA 16% calculado',
          value: iva16,
        },
        {
          key: 'iva8',
          label: 'IVA 8% calculado',
          value: iva8,
        },
      ];
    },
  },
  methods: {
    roundDiotInteger(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return 0;
      }

      const floor = Math.floor(number);
      const fraction = number - floor;

      // Regla DIOT: los .5 exactos redondean hacia abajo.
      if (Math.abs(fraction - 0.5) < 1e-9) {
        return floor;
      }

      return Math.round(number);
    },
    getNumericDiotKeys() {
      return (this.diotColumns || [])
        .filter((column) => column?.type === 'number')
        .map((column) => column.key);
    },
    normalizeSupplierSelections(selections) {
      const source = selections && typeof selections === 'object' ? selections : {};
      const numericKeys = new Set(this.getNumericDiotKeys());
      const normalized = {};

      Object.keys(source).forEach((supplierKey) => {
        const row = source[supplierKey] && typeof source[supplierKey] === 'object' ? source[supplierKey] : {};
        const nextRow = { ...row };

        numericKeys.forEach((key) => {
          nextRow[key] = this.roundDiotInteger(nextRow[key] ?? 0);
        });

        normalized[supplierKey] = nextRow;
      });

      return normalized;
    },
    getOptionValue(option) {
      if (option && typeof option === 'object') {
        return String(option.value ?? '');
      }

      return String(option ?? '');
    },
    getOptionLabel(option) {
      if (option && typeof option === 'object') {
        return String(option.label ?? option.value ?? '');
      }

      return String(option ?? '');
    },
    getSupplierKey(item) {
      const explicitKey = String(item?.supplier_key || '').trim().toUpperCase();
      if (explicitKey) {
        return explicitKey;
      }

      const rfc = String(item?.provider_rfc || '').trim().toUpperCase();
      const name = String(item?.provider_name || '').trim().toUpperCase();
      return `${rfc}|${name}`;
    },
    buildDefaultDiotSelection(item) {
      const subtotalMxn = this.roundDiotInteger(item?.subtotal_mxn || 0);
      const ivaAcreditable = this.roundDiotInteger(Number(item?.iva_16_mxn || 0) + Number(item?.iva_8_mxn || 0));

      return {
        f01_tipo_tercero: '04',
        f02_tipo_operacion: '85',
        f03_rfc: String(item?.provider_rfc || '').trim().toUpperCase(),
        f04_id_fiscal: '',
        f05_nombre_extranjero: '',
        f06_pais_residencia: '',
        f07_jurisdiccion_fiscal: '',
        f08_actividades_pagadas_rfn: 0,
        f09_devoluciones_rfn: 0,
        f10_actividades_pagadas_rfs: 0,
        f11_devoluciones_rfs: 0,
        f12_actividades_pagadas_resto: subtotalMxn,
        f13_devoluciones_resto: 0,
        f14_importaciones_16: 0,
        f15_devoluciones_importaciones: 0,
        f16_importaciones_intangibles_16: 0,
        f17_devoluciones_importaciones_intangibles: 0,
        f18_iva_acreditable_rfn: 0,
        f19_proporcion_rfn: 0,
        f20_iva_acreditable_rfs: 0,
        f21_proporcion_rfs: 0,
        f22_iva_acreditable_resto: ivaAcreditable,
        f23_proporcion_resto: 0,
        f24_iva_acreditable_import_tang: 0,
        f25_proporcion_import_tang: 0,
        f26_iva_acreditable_import_intang: 0,
        f27_proporcion_import_intang: 0,
        f28_iva_no_acreditable_prop_rfn: 0,
        f29_iva_no_acreditable_no_req_rfn: 0,
        f30_iva_no_acreditable_exentas_rfn: 0,
        f31_iva_no_acreditable_no_objeto_rfn: 0,
        f32_iva_no_acreditable_prop_rfs: 0,
        f33_iva_no_acreditable_no_req_rfs: 0,
        f34_iva_no_acreditable_exentas_rfs: 0,
        f35_iva_no_acreditable_no_objeto_rfs: 0,
        f36_iva_no_acreditable_prop_resto: 0,
        f37_iva_no_acreditable_no_req_resto: 0,
        f38_iva_no_acreditable_exentas_resto: 0,
        f39_iva_no_acreditable_no_objeto_resto: 0,
        f40_iva_no_acreditable_prop_imp_tang: 0,
        f41_iva_no_acreditable_no_req_imp_tang: 0,
        f42_iva_no_acreditable_exentas_imp_tang: 0,
        f43_iva_no_acreditable_no_objeto_imp_tang: 0,
        f44_iva_no_acreditable_prop_imp_intang: 0,
        f45_iva_no_acreditable_no_req_imp_intang: 0,
        f46_iva_no_acreditable_exentas_imp_intang: 0,
        f47_iva_no_acreditable_no_objeto_imp_intang: 0,
        f48_iva_retenido_contribuyente: this.roundDiotInteger(item?.ret_iva_mxn || 0),
        f49_importacion_exentos: 0,
        f50_actos_exentos: this.roundDiotInteger(item?.base_exento_mxn || 0),
        f51_actos_tasa_0: this.roundDiotInteger(item?.base_0_mxn || 0),
        f52_actos_no_objeto_nacional: 0,
        f53_actos_no_objeto_sin_establecimiento: 0,
        f54_efectos_fiscales: '01',
      };
    },
    ensureSupplierSelection(item) {
      const key = this.getSupplierKey(item);
      if (!key) {
        return null;
      }

      if (!this.supplierSelections[key]) {
        this.supplierSelections[key] = this.buildDefaultDiotSelection(item);
      }

      return this.supplierSelections[key];
    },
    getDiotValue(item, key) {
      const supplierKey = this.getSupplierKey(item);
      if (!supplierKey) {
        return '';
      }

      const selection = this.supplierSelections[supplierKey];
      if (selection) {
        return selection[key] ?? '';
      }

      const fallback = this.buildDefaultDiotSelection(item);
      return fallback[key] ?? '';
    },
    setDiotValue(item, key, value, type = 'text') {
      const selection = this.ensureSupplierSelection(item);
      if (!selection) {
        return;
      }

      if (type === 'number') {
        selection[key] = this.roundDiotInteger(value);
        this.refreshKpisFromSupplierSelections();
        return;
      }

      selection[key] = String(value ?? '').trim();
      this.refreshKpisFromSupplierSelections();
    },
    refreshKpisFromSupplierSelections() {
      const rows = (this.summary || []).map((item) => {
        const supplierKey = this.getSupplierKey(item);
        if (!supplierKey) {
          return null;
        }

        return this.supplierSelections?.[supplierKey] || this.buildDefaultDiotSelection(item);
      }).filter(Boolean);

      if (!rows.length) {
        this.totals = {
          ...(this.totals || {}),
          base_8_mxn: 0,
          base_16_mxn: 0,
          base_exento_mxn: 0,
          base_0_mxn: 0,
          ret_iva_mxn: 0,
          iva_8_mxn: 0,
          iva_16_mxn: 0,
        };
        return;
      }

      const sum = (keys) => rows.reduce((total, row) => {
        const rowTotal = (keys || []).reduce((acc, key) => acc + this.roundDiotInteger(row?.[key] ?? 0), 0);
        return total + rowTotal;
      }, 0);

      const base8 = sum(['f08_actividades_pagadas_rfn', 'f10_actividades_pagadas_rfs']);
      const base16 = sum(['f12_actividades_pagadas_resto', 'f14_importaciones_16', 'f16_importaciones_intangibles_16']);
      const baseExento = sum(['f49_importacion_exentos', 'f50_actos_exentos']);
      const base0 = sum(['f51_actos_tasa_0']);
      const retainedIva = sum(['f48_iva_retenido_contribuyente']);

      const iva8 = sum(['f18_iva_acreditable_rfn', 'f19_proporcion_rfn', 'f20_iva_acreditable_rfs', 'f21_proporcion_rfs']);
      const iva16 = sum([
        'f22_iva_acreditable_resto',
        'f23_proporcion_resto',
        'f24_iva_acreditable_import_tang',
        'f25_proporcion_import_tang',
        'f26_iva_acreditable_import_intang',
        'f27_proporcion_import_intang',
      ]);

      this.totals = {
        ...(this.totals || {}),
        base_8_mxn: this.roundDiotInteger(base8),
        base_16_mxn: this.roundDiotInteger(base16),
        base_exento_mxn: this.roundDiotInteger(baseExento),
        base_0_mxn: this.roundDiotInteger(base0),
        ret_iva_mxn: this.roundDiotInteger(retainedIva),
        iva_8_mxn: this.roundDiotInteger(iva8),
        iva_16_mxn: this.roundDiotInteger(iva16),
      };
    },
    getColumnHelp(column) {
      const code = String(column?.code || '').trim();
      const label = String(column?.label || '').trim();
      const base = `${code} - ${label}`;
      const detail = String(column?.help || '').trim();

      if (!detail) {
        return base;
      }

      return `${base}<br>${detail}`;
    },
    initializeSupplierSelections() {
      (this.summary || []).forEach((item) => {
        this.ensureSupplierSelection(item);
      });
    },
    initHelpPopovers() {
      this.disposeHelpPopovers();
      const elements = this.$el?.querySelectorAll('[data-bs-toggle="popover"]') || [];
      this.helpPopovers = Array.from(elements).map((el) => {
        try {
          return new Popover(el, { container: 'body' });
        } catch (error) {
          return null;
        }
      }).filter(Boolean);
    },
    disposeHelpPopovers() {
      (this.helpPopovers || []).forEach((popover) => {
        try {
          popover.dispose();
        } catch (error) {
          // noop
        }
      });
      this.helpPopovers = [];
    },
    handleGlobalPeriodChanged(event) {
      const nextPeriod = String(event?.detail?.period || '').trim() || getGlobalPeriod();
      if (nextPeriod === this.period) {
        return;
      }

      this.period = nextPeriod;
      this.fetchReport();
    },
    async fetchReport() {
      this.loading = true;
      this.error = '';
      this.saveMessage = '';

      try {
        const response = await ws.getDiotWorkpaper(this.rfc, this.period);

        if (response && response.type === 'success') {
          this.applyDiotPayload(response?.data || {});
          return;
        }

        this.error = response?.message || 'No fue posible generar el papel de trabajo DIOT.';
      } catch (error) {
        this.error = error?.response?.data?.message || 'No fue posible generar el papel de trabajo DIOT.';
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.initHelpPopovers();
        });
      }
    },
    async downloadExcel() {
      try {
        const blob = await ws.exportDiotWorkpaperExcel(
          this.rfc,
          this.period,
          this.supplierSelections,
          this.deletedSuppliers
        );
        this.downloadBlob(blob, `diot-papel-trabajo-${this.period || 'periodo'}.xlsx`);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No fue posible exportar el archivo Excel.';
      }
    },
    async downloadTxt() {
      try {
        const blob = await ws.exportDiotWorkpaperTxt(
          this.rfc,
          this.period,
          this.supplierSelections,
          this.deletedSuppliers
        );
        this.downloadBlob(blob, `diot-papel-trabajo-${this.period || 'periodo'}.txt`);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No fue posible exportar el archivo TXT.';
      }
    },
    toggleExportMenu() {
      if (this.loading || this.saving || this.recalculating || this.deletingSupplier || this.manualSupplierSaving || (!this.details.length && !this.summary.length)) {
        return;
      }

      this.exportMenuOpen = !this.exportMenuOpen;
    },
    handleDocumentClick(event) {
      const clickedInsideExport = event?.target?.closest?.('.diot-export-group');
      if (!clickedInsideExport) {
        this.exportMenuOpen = false;
      }
    },
    handleExportOption(type) {
      if (this.loading || this.saving || this.recalculating || this.deletingSupplier || this.manualSupplierSaving) {
        return;
      }

      if (type === 'excel') {
        if (!this.details.length) {
          return;
        }
        this.exportMenuOpen = false;
        this.downloadExcel();
        return;
      }

      if (type === 'txt') {
        if (!this.summary.length) {
          return;
        }
        this.exportMenuOpen = false;
        this.downloadTxt();
      }
    },
    async saveDiot() {
      this.saving = true;
      this.error = '';
      this.saveMessage = '';

      try {
        const response = await ws.saveDiotWorkpaper(this.rfc, this.period, this.supplierSelections, this.deletedSuppliers);
        if (response && response.type === 'success') {
          this.applyDiotPayload(response?.data || {});
          this.saveMessage = `DIOT guardada correctamente${this.savedAt ? ` (${this.savedAt})` : ''}.`;
          return;
        }

        this.error = response?.message || 'No fue posible guardar la DIOT.';
      } catch (error) {
        this.error = error?.response?.data?.message || 'No fue posible guardar la DIOT.';
      } finally {
        this.saving = false;
      }
    },
    openRecalculateModal() {
      this.recalculateModal.visible = true;
    },
    openManualTypeModal() {
      this.manualTypeModal.visible = true;
    },
    closeManualTypeModal() {
      this.manualTypeModal.visible = false;
    },
    selectManualSupplierType(type) {
      this.closeManualTypeModal();
      this.manualSupplierErrors = {};
      this.manualSupplierFormError = '';
      this.manualSupplierForm = {
        type,
        provider_rfc: type === 'global' ? 'XAXX010101000' : (type === 'international' ? 'XEXX010101000' : ''),
        operation_type: type === 'global' ? '87' : '85',
        provider_name: '',
        tax_id: '',
        f06_pais_residencia: '',
        f12_actividades_pagadas_resto: 0,
        f22_iva_acreditable_resto: 0,
        f50_actos_exentos: 0,
        f51_actos_tasa_0: 0,
        f52_actos_no_objeto_nacional: 0,
      };

      if (type === 'national') {
        this.manualSupplierModal.title = 'Proveedor Nacional (manual)';
      }
      if (type === 'international') {
        this.manualSupplierModal.title = 'Proveedor Internacional (manual)';
      }
      if (type === 'global') {
        this.manualSupplierModal.title = 'Proveedor Global (manual)';
      }

      this.manualSupplierModal.visible = true;
    },
    closeManualSupplierModal(force = false) {
      if (this.manualSupplierSaving && !force) {
        return;
      }

      this.manualSupplierModal.visible = false;
      this.manualSupplierErrors = {};
      this.manualSupplierFormError = '';
    },
    validateManualSupplierForm() {
      const type = String(this.manualSupplierForm.type || '').trim();
      const errors = {};

      this.normalizeManualAmounts();

      const amounts = [
        this.roundDiotInteger(this.manualSupplierForm.f12_actividades_pagadas_resto),
        this.roundDiotInteger(this.manualSupplierForm.f22_iva_acreditable_resto),
        this.roundDiotInteger(this.manualSupplierForm.f50_actos_exentos),
        this.roundDiotInteger(this.manualSupplierForm.f51_actos_tasa_0),
        this.roundDiotInteger(this.manualSupplierForm.f52_actos_no_objeto_nacional),
      ];
      const hasAtLeastOnePositiveAmount = amounts.some((amount) => Number(amount) > 0);

      if (type === 'national') {
        const rfc = String(this.manualSupplierForm.provider_rfc || '').trim().toUpperCase();
        if (!rfc) {
          errors.provider_rfc = 'El RFC es obligatorio para proveedor nacional.';
        }
      }

      if (type === 'international') {
        const providerName = String(this.manualSupplierForm.provider_name || '').trim();
        const taxId = String(this.manualSupplierForm.tax_id || '').trim().toUpperCase();
        const fiscalCountry = String(this.manualSupplierForm.f06_pais_residencia || '').trim();

        if (!providerName) {
          errors.provider_name = 'El nombre del proveedor es obligatorio.';
        }
        if (!taxId) {
          errors.tax_id = 'El ID fiscal es obligatorio.';
        }
        if (!fiscalCountry) {
          errors.f06_pais_residencia = 'El país de residencia fiscal es obligatorio.';
        }
      }

      if (!hasAtLeastOnePositiveAmount) {
        errors.amounts = 'La suma de los montos debe ser mayor que 0.';
      }

      this.manualSupplierErrors = errors;
      return Object.keys(errors).length === 0;
    },
    normalizeManualAmounts() {
      this.sanitizeManualAmount('f12_actividades_pagadas_resto');
      this.sanitizeManualAmount('f22_iva_acreditable_resto');
      this.sanitizeManualAmount('f50_actos_exentos');
      this.sanitizeManualAmount('f51_actos_tasa_0');
      this.sanitizeManualAmount('f52_actos_no_objeto_nacional');
    },
    sanitizeManualAmount(field, options = {}) {
      const rawValue = this.manualSupplierForm?.[field];
      const numericValue = Number(rawValue);

      let normalized = Number.isFinite(numericValue) ? this.roundDiotInteger(numericValue) : 0;
      if (normalized < 0) {
        normalized = 0;
      }

      this.manualSupplierForm[field] = normalized;

      if (options?.syncIva) {
        const base16 = this.roundDiotInteger(this.manualSupplierForm.f12_actividades_pagadas_resto || 0);
        this.manualSupplierForm.f22_iva_acreditable_resto = this.roundDiotInteger(base16 * 0.16);
      }
    },
    async submitManualSupplier() {
      if (this.manualSupplierSaving) {
        return;
      }

      this.manualSupplierErrors = {};
      this.manualSupplierFormError = '';

      if (!this.validateManualSupplierForm()) {
        return;
      }

      const payload = {
        manual_type: this.manualSupplierForm.type,
        provider_rfc: String(this.manualSupplierForm.provider_rfc || '').trim().toUpperCase(),
        f12_actividades_pagadas_resto: this.roundDiotInteger(this.manualSupplierForm.f12_actividades_pagadas_resto),
        f22_iva_acreditable_resto: this.roundDiotInteger(this.manualSupplierForm.f22_iva_acreditable_resto),
        f50_actos_exentos: this.roundDiotInteger(this.manualSupplierForm.f50_actos_exentos),
        f51_actos_tasa_0: this.roundDiotInteger(this.manualSupplierForm.f51_actos_tasa_0),
        f52_actos_no_objeto_nacional: this.roundDiotInteger(this.manualSupplierForm.f52_actos_no_objeto_nacional),
      };

      if (this.manualSupplierForm.type === 'national' || this.manualSupplierForm.type === 'global') {
        payload.provider_rfc = String(this.manualSupplierForm.provider_rfc || '').trim().toUpperCase();
        payload.operation_type = String(this.manualSupplierForm.operation_type || (this.manualSupplierForm.type === 'global' ? '87' : '85')).trim().toUpperCase();
      }

      if (this.manualSupplierForm.type === 'international') {
        payload.provider_name = String(this.manualSupplierForm.provider_name || '').trim();
        payload.tax_id = String(this.manualSupplierForm.tax_id || '').trim().toUpperCase();
        payload.f06_pais_residencia = String(this.manualSupplierForm.f06_pais_residencia || '').trim();
      }

      this.manualSupplierSaving = true;
      this.saveMessage = '';

      try {
        const response = await ws.addManualDiotSupplier(this.rfc, this.period, payload);
        if (response && response.type === 'success') {
          this.applyDiotPayload(response?.data || {});
          this.saveMessage = response?.message || 'Proveedor manual agregado correctamente.';
          this.closeManualSupplierModal(true);
          return;
        }

        this.manualSupplierFormError = response?.message || 'No fue posible agregar el proveedor manual.';
      } catch (error) {
        this.manualSupplierFormError = error?.response?.data?.message || 'No fue posible agregar el proveedor manual.';
      } finally {
        this.manualSupplierSaving = false;
      }
    },
    closeRecalculateModal(force = false) {
      if (this.recalculating && !force) {
        return;
      }

      this.recalculateModal.visible = false;
    },
    openIvaBreakdownModal() {
      this.ivaBreakdownModal.visible = true;
    },
    closeIvaBreakdownModal() {
      this.ivaBreakdownModal.visible = false;
    },
    openIvaTaxBreakdownModal() {
      this.ivaTaxBreakdownModal.visible = true;
    },
    closeIvaTaxBreakdownModal() {
      this.ivaTaxBreakdownModal.visible = false;
    },
    async confirmRecalculateDiot() {
      if (this.recalculating) {
        return;
      }

      await this.recalculateDiot();
    },
    async recalculateDiot() {
      this.closeRecalculateModal(true);

      this.recalculating = true;
      this.error = '';
      this.saveMessage = '';

      try {
        const response = await ws.recalculateDiotWorkpaper(this.rfc, this.period);
        if (response && response.type === 'success') {
          this.applyDiotPayload(response?.data || {});
          this.saveMessage = 'DIOT recalculada con base en los CFDIs registrados.';
          return;
        }

        this.error = response?.message || 'No fue posible recalcular la DIOT.';
      } catch (error) {
        this.error = error?.response?.data?.message || 'No fue posible recalcular la DIOT.';
      } finally {
        this.recalculating = false;
      }
    },
    openDeleteSupplierModal(item) {
      const providerRfc = String(item?.provider_rfc || '').trim().toUpperCase();
      const providerName = String(item?.provider_name || '').trim();

      if (!providerRfc && !providerName) {
        return;
      }

      this.deleteSupplierModal.item = {
        supplier_key: String(item?.supplier_key || '').trim(),
        provider_rfc: providerRfc,
        provider_name: providerName,
      };
      this.deleteSupplierModal.message = `Se eliminará el proveedor ${providerName || providerRfc} de la DIOT del periodo ${this.period}. Esta acción afectará la descarga del Excel/TXT. ¿Deseas continuar?`;
      this.deleteSupplierModal.visible = true;
    },
    closeDeleteSupplierModal(force = false) {
      if (this.deletingSupplier && !force) {
        return;
      }

      this.deleteSupplierModal.visible = false;
      this.deleteSupplierModal.item = null;
      this.deleteSupplierModal.message = '¿Deseas eliminar este proveedor de la DIOT para el periodo actual?';
    },
    async confirmDeleteSupplier() {
      if (this.deletingSupplier || !this.deleteSupplierModal.item) {
        return;
      }

      this.deletingSupplier = true;
      this.error = '';
      this.saveMessage = '';

      try {
        const response = await ws.deleteDiotWorkpaperSupplier(
          this.rfc,
          this.period,
          this.deleteSupplierModal.item.provider_rfc,
          this.deleteSupplierModal.item.provider_name,
          this.supplierSelections,
          this.deletedSuppliers,
          this.deleteSupplierModal.item.supplier_key
        );

        if (response && response.type === 'success') {
          this.applyDiotPayload(response?.data || {});
          this.saveMessage = response?.message || 'Proveedor eliminado de la DIOT para el periodo seleccionado.';
          this.closeDeleteSupplierModal(true);
          return;
        }

        this.error = response?.message || 'No fue posible eliminar el proveedor de la DIOT.';
      } catch (error) {
        this.error = error?.response?.data?.message || 'No fue posible eliminar el proveedor de la DIOT.';
      } finally {
        this.deletingSupplier = false;
      }
    },
    applyDiotPayload(payload) {
      this.summary = payload?.summary || [];
      this.details = payload?.details || [];
      this.totals = payload?.totals || this.totals;
      this.source = payload?.source || 'calculated';
      this.savedAt = payload?.saved_at || null;
      this.deletedSuppliers = Array.isArray(payload?.deleted_suppliers) ? payload.deleted_suppliers : [];
      this.supplierSelections = this.normalizeSupplierSelections(payload?.supplier_selections || {});
      this.initializeSupplierSelections();
      this.refreshKpisFromSupplierSelections();
    },
    downloadBlob(payload, fallbackName) {
      const blob = payload instanceof Blob ? payload : new Blob([payload]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fallbackName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    openCfdiPreview(uuid) {
      const normalized = String(uuid || '').trim().toUpperCase();
      if (!normalized) {
        return;
      }

      this.cfdiPreviewModal.uuid = normalized;
      this.cfdiPreviewModal.visible = true;
    },
    closeCfdiPreview() {
      this.cfdiPreviewModal.visible = false;
      this.cfdiPreviewModal.uuid = '';
    },
    formatUuid(value) {
      if (!value) {
        return '-';
      }

      return String(value).toUpperCase();
    },
    formatInteger(value) {
      return Number(value || 0).toLocaleString('es-MX');
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(value) {
      if (!value) {
        return '-';
      }

      const date = String(value).split(' ')[0];
      const [year, month, day] = date.split('-');
      if (!year || !month || !day) {
        return value;
      }

      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.diot-header {
  gap: 12px;
}

.diot-actions {
  gap: 8px;
}

.diot-period-chip {
  border: 1px solid rgba(47, 138, 182, 0.25);
  color: #2f8ab6;
  border-radius: 999px;
  padding: 4px 10px;
  font-weight: 600;
}

.diot-kpi {
  position: relative;
  border: 1px solid rgba(47, 138, 182, 0.15);
  border-radius: 10px;
  padding: 10px 12px;
  background: rgba(47, 138, 182, 0.04);
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--theme-font-color);
}

.diot-kpi strong {
  font-size: 18px;
  color: #2f8ab6;
}

.diot-kpi-split {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.diot-kpi-metric {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.diot-kpi-metric-right {
  text-align: right;
}

.diot-kpi-button {
  position: absolute;
  top: 6px;
  right: 8px;
  margin: 0;
  padding: 2px 8px;
  font-size: 11px;
  line-height: 1;
}

.diot-modal-kpi {
  border: 1px solid rgba(47, 138, 182, 0.18);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(47, 138, 182, 0.04);
}

.diot-modal-kpi strong {
  color: #2f8ab6;
}

.diot-table {
  font-size: 12px;
}

.diot-wide-wrap {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.diot-wide-table {
  min-width: 4800px;
}

.diot-wide-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
}

.diot-actions-col {
  min-width: 110px;
  width: 110px;
  text-align: center;
}

.diot-th-wrap {
  display: flex;
  align-items: flex-start;
  gap: 0.3rem;
  min-width: 130px;
  max-width: 160px;
  white-space: nowrap;
  vertical-align: top;
  cursor: help;
}

.diot-th-code {
  flex: 0 0 auto;
  min-width: 1.9rem;
  text-align: center;
  font-size: 11px;
  line-height: 1.2;
  font-weight: 700;
  color: #4c5667;
  background: #f3f6fb;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 2px 4px;
}

.diot-th-label {
  display: inline-block;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 1.2;
}

.diot-cell {
  min-width: 130px;
}

.diot-input {
  min-width: 120px;
}

.diot-select {
  min-width: 110px;
  font-size: 12px;
}

.diot-readonly {
  background: #f8f9fa;
}

.diot-uuid {
  max-width: 220px;
}

.uuid-link-btn {
  background: none;
  border: none;
  padding: 0;
  color: var(--theme-deafult);
  font-size: 10px;
  font-family: monospace;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: pointer;
  white-space: nowrap;
  max-width: 220px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.uuid-link-btn:hover {
  text-decoration: underline solid;
}

.uuid-link-btn:focus {
  outline: none;
}

.uuid-link-btn:focus-visible {
  outline: 2px solid rgba(115, 102, 255, 0.35);
  outline-offset: 2px;
}
</style>

<style>
.popover.diot-help-popover {
  max-width: 280px;
  font-size: 12px;
  background-color: #f3ecff !important;
  border: 2px solid #b697f0 !important;
  box-shadow: 0 10px 24px rgba(78, 48, 138, 0.18) !important;
  color: #3f2a66 !important;
  --bs-popover-bg: #f3ecff !important;
  --bs-popover-border-color: #b697f0 !important;
  --bs-popover-body-color: #3f2a66 !important;
  --bs-popover-header-bg: #e9dbff !important;
  --bs-popover-header-color: #3a255f !important;
}

.popover.diot-help-popover .popover-body {
  background-color: #f3ecff !important;
  color: #3f2a66 !important;
}

.popover.diot-help-popover .popover-header {
  background-color: #e9dbff !important;
  color: #3a255f !important;
  border-bottom-color: #b697f0 !important;
}

.bs-popover-top.diot-help-popover > .popover-arrow::after,
.bs-popover-auto[data-popper-placement^=top].diot-help-popover > .popover-arrow::after {
  border-top-color: #f3ecff !important;
}

.bs-popover-end.diot-help-popover > .popover-arrow::after,
.bs-popover-auto[data-popper-placement^=right].diot-help-popover > .popover-arrow::after {
  border-right-color: #f3ecff !important;
}

.bs-popover-bottom.diot-help-popover > .popover-arrow::after,
.bs-popover-auto[data-popper-placement^=bottom].diot-help-popover > .popover-arrow::after {
  border-bottom-color: #f3ecff !important;
}

.bs-popover-start.diot-help-popover > .popover-arrow::after,
.bs-popover-auto[data-popper-placement^=left].diot-help-popover > .popover-arrow::after {
  border-left-color: #f3ecff !important;
}
</style>
